import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css"; 

import { getDailyAnalytics } from "../Service/apiService";

const DailyDataTable = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab
  const { pathname } = location;
  const activeTab = pathname.startsWith("/daily-data-table")
    ? "daily-data-table"
    : pathname.startsWith("/analytics")
    ? "analytics"
    : "dashboard";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getDailyAnalytics();
        
        setData(res?.data || []);
      } catch (error) {
        console.error("Failed to fetch daily analytics:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatNumber = (num) => {
    if (num == null || num === "") return "—";
    return Number(num).toLocaleString();
  };

  const columns = [
    { key: "date", label: "Date" },
    { key: "dailyLoginCount", label: "Daily Login Count" },
    { key: "dailyFailedLoginCount", label: "Daily Failed Login Count" },
    { key: "totalLoginCount", label: "Total Login Count" },
    { key: "totalFailedLoginCount", label: "Total Failed Login Count" },
    { key: "dailySearchCount", label: "Daily Search Count" },
    { key: "totalSearchCount", label: "Total Search Count" },
    { key: "totalCompanies", label: "Total Companies" },
    { key: "increasedCompanyCount", label: "Increased Company Count" },
    { key: "activeCompanies", label: "Active Companies" },
    { key: "lostCompanies", label: "Lost Companies" },
    { key: "renewedCompanies", label: "Renewed Companies" },
    { key: "totalUsers", label: "Total Users" },
    { key: "increasedUserCount", label: "Increased User Count" },
    { key: "activeUsers", label: "Active Users" },
    { key: "inactiveUsers", label: "Inactive Users" },
    { key: "newUsersToday", label: "New Users Today" },
    { key: "lostUsers", label: "Lost Users" },
    { key: "oneWeekSearchCount", label: "One Week Search Count" },
  ];

  return (
    <div className="daily-data-table-container">
      {/* Top Navigation Tabs — always visible */}
      <div className="daily-data-tabs">
        <button
          onClick={() => navigate("/analytics")}
          className={activeTab === "analytics" ? "tab-btn active" : "tab-btn"}
        >
          Analytics
        </button>
        <button
          onClick={() => navigate("/daily-data-table")}
          className={
            activeTab === "daily-data-table" ? "tab-btn active" : "tab-btn"
          }
        >
          Daily Data Table
        </button>
        <button
          onClick={() => navigate("/dashboard/companies")}
          className={activeTab === "dashboard" ? "tab-btn active" : "tab-btn"}
        >
          Dashboard
        </button>
      </div>

      {/* Loading Skeleton — shown ONLY while loading */}
      {loading ? (
        <div className="skeleton-container" style={{ padding: "20px" }}>
          {/* Header row skeleton */}
          <div style={{ marginBottom: "16px" }}>
            <Skeleton height={24} width="100%" />
          </div>

          {/* 5 skeleton rows */}
          {Array.from({ length: 5 }).map((_, idx) => (
            <div
              key={`skeleton-row-${idx}`}
              style={{
                display: "flex",
                gap: "12px",
                marginBottom: "12px",
                padding: "12px",
                borderRadius: "6px",
                backgroundColor: "#f9f9f9"
              }}
            >
              {columns.map((_, colIdx) => (
                <div key={`skeleton-cell-${colIdx}`} style={{ flex: 1, minWidth: 100 }}>
                  <Skeleton height={18} />
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        /* Render actual table only when NOT loading */
        <div className="daily-data-table-wrapper">
          <table className="daily-data-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className="daily-data-table-header">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((row) => (
                  <tr key={row._id || row.date} className="daily-data-table-row">
                    {columns.map((col) => (
                      <td key={col.key} className="daily-data-table-cell">
                        {col.key === "date" ? (
                          row[col.key]
                        ) : col.key === "dailyFailedLoginCount" && row[col.key] > 0 ? (
                          <span
                            className="clickable-count"
                            onClick={() => navigate(`/failed-logins/${row.date}`)}
                            title="View failed login details"
                          >
                            {formatNumber(row[col.key])}
                          </span>
                        ) : (
                          formatNumber(row[col.key])
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="daily-data-table-empty">
                    No data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DailyDataTable;